import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, Search, Filter, Plus, Printer, Eye,
  Calendar, Clock, CheckCircle, XCircle, Truck, ChefHat, AlertCircle, LogOut,
  Link2, Timer, ExternalLink, Route, Snowflake, Database
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import LanguageSwitcher from '../components/LanguageSwitcher';

function AdminOrders({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const ORDER_STATUSES = [
    { value: 'Pending', label: t('adminOrders.statusPending'), color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
    { value: 'Accepted', label: t('adminOrders.statusAccepted'), color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
    { value: 'In Production', label: t('adminOrders.statusInProduction'), color: 'bg-purple-100 text-purple-800', icon: ChefHat },
    { value: 'Ready', label: t('adminOrders.statusReady'), color: 'bg-green-100 text-green-800', icon: Package },
    { value: 'Delivered', label: t('adminOrders.statusDelivered'), color: 'bg-emerald-100 text-emerald-800', icon: Truck },
    { value: 'Cancelled', label: t('adminOrders.statusCancelled'), color: 'bg-red-100 text-red-800', icon: XCircle },
  ];
  
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'all',
    clientType: searchParams.get('clientType') || 'all',
    clientSearch: searchParams.get('search') || '',
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || '',
    deliveryDate: searchParams.get('deliveryDate') || '',
  });
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showTrackingDialog, setShowTrackingDialog] = useState(false);
  const [showCookingTimeDialog, setShowCookingTimeDialog] = useState(false);
  const [trackingLink, setTrackingLink] = useState('');
  const [cookingTime, setCookingTime] = useState(20);
  
  const [newOrder, setNewOrder] = useState({
    user_id: '',
    items: [],
    delivery_date: '',
    delivery_time: '',
    delivery_cost: 0,
    notes: '',
  });
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [filters.dateFrom, filters.dateTo, filters.status]);

  const fetchData = async () => {
    try {
      // Server-side filters we forward to /orders: date range + status.
      // Everything else (client search, clientType) is applied on the client side
      // over the already-paginated result.
      const params = new URLSearchParams({ limit: '500' });
      if (filters.dateFrom) params.set('from_date', filters.dateFrom);
      if (filters.dateTo) params.set('to_date', filters.dateTo);
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);

      const results = await Promise.allSettled([
        axios.get(`${API}/orders?${params.toString()}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/products`, { headers: getAuthHeaders() }),
      ]);
      if (results[0].status === 'fulfilled') {
        setOrders(results[0].value.data);
        const total = parseInt(results[0].value.headers['x-total-count'] || results[0].value.data.length, 10);
        setTotalOrders(isNaN(total) ? results[0].value.data.length : total);
      }
      if (results[1].status === 'fulfilled') setClients(results[1].value.data.filter(u => u.role !== 'Admin'));
      if (results[2].status === 'fulfilled') setProducts(results[2].value.data);
    } catch (error) {
      toast.error(t('admin.failed'));
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filters.status !== 'all' && order.status !== filters.status) return false;
    if (filters.clientType !== 'all' && order.user_role !== filters.clientType) return false;
    if (filters.clientSearch) {
      const search = filters.clientSearch.toLowerCase();
      const matchName = order.user_name?.toLowerCase().includes(search);
      const matchEmail = order.user_email?.toLowerCase().includes(search);
      const matchOrder = order.order_number?.toLowerCase().includes(search);
      if (!matchName && !matchEmail && !matchOrder) return false;
    }
    if (filters.dateFrom) {
      const orderDate = new Date(order.created_at);
      const fromDate = new Date(filters.dateFrom);
      if (orderDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const orderDate = new Date(order.created_at);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59);
      if (orderDate > toDate) return false;
    }
    if (filters.deliveryDate && order.delivery_date !== filters.deliveryDate) return false;
    return true;
  });

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      toast.success(t('admin.update'));
      fetchData();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error(t('admin.failed'));
    }
  };

  const handleSetCookingTime = async () => {
    if (!selectedOrder) return;
    try {
      await axios.put(
        `${API}/admin/orders/${selectedOrder.id}/cooking-time?cooking_time=${cookingTime}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Время готовки установлено');
      setShowCookingTimeDialog(false);
      fetchData();
      setSelectedOrder(prev => ({ ...prev, estimated_cooking_time: cookingTime }));
    } catch (error) {
      toast.error('Ошибка сохранения');
    }
  };

  const [rebuildingRoute, setRebuildingRoute] = useState(false);

  const handleRebuildRoute = async (order) => {
    if (!order?.delivery_date) {
      toast.error('У заказа не указана дата доставки');
      return;
    }
    setRebuildingRoute(true);
    try {
      const res = await axios.post(
        `${API}/logistics/build-route?target_date=${order.delivery_date}`,
        {},
        { headers: getAuthHeaders() }
      );
      const count = res.data?.orders ?? 0;
      if (count === 0) {
        toast.warning(`Нет подтверждённых заказов на ${order.delivery_date}`);
      } else {
        toast.success(`Маршрут отправлен в Telegram (${count} точек). Откройте группу логистики.`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(`Ошибка: ${detail}`);
    } finally {
      setRebuildingRoute(false);
    }
  };

  const handleOpenBulkWaybills = async () => {
    try {
      const res = await axios.get(`${API}/orders/waybills/bulk.pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Накладные на завтра сформированы');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось сформировать');
    }
  };

  const handleOpenWaybill = async (order) => {
    if (!order?.id) return;
    try {
      const res = await axios.get(`${API}/orders/${order.id}/waybill.pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // Open in a new tab; user reviews and prints with Ctrl+P.
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after 60s to free memory (browser has already loaded it).
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось сгенерировать накладную');
    }
  };

  const handleUnfreeze1C = async (order) => {
    if (!order?.id) return;
    try {
      const res = await axios.post(
        `${API}/admin/orders/${order.id}/unfreeze-1c`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || 'Заморозка снята');
      // refresh so the button reflects the new state
      setSelectedOrder(prev => prev ? { ...prev, sync_frozen_after: null } : prev);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось снять заморозку');
    }
  };

  const handleSetTrackingLink = async () => {
    if (!selectedOrder || !trackingLink) return;
    try {
      await axios.put(
        `${API}/admin/orders/${selectedOrder.id}/tracking?tracking_link=${encodeURIComponent(trackingLink)}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Ссылка отслеживания добавлена');
      setShowTrackingDialog(false);
      fetchData();
      setSelectedOrder(prev => ({ ...prev, tracking_link: trackingLink, status: 'Delivering' }));
    } catch (error) {
      toast.error('Ошибка сохранения');
    }
  };

  const openCookingTimeDialog = (order) => {
    setSelectedOrder(order);
    setCookingTime(order.estimated_cooking_time || 20);
    setShowCookingTimeDialog(true);
  };

  const openTrackingDialog = (order) => {
    setSelectedOrder(order);
    setTrackingLink(order.tracking_link || '');
    setShowTrackingDialog(true);
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!newOrder.user_id || newOrder.items.length === 0) {
      toast.error(t('adminOrders.selectClient'));
      return;
    }
    
    try {
      await axios.post(`${API}/admin/orders`, {
        ...newOrder,
        items: newOrder.items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity
        }))
      }, { headers: getAuthHeaders() });
      
      toast.success(t('adminOrders.createOrder'));
      setShowCreateOrder(false);
      setNewOrder({ user_id: '', items: [], delivery_date: '', delivery_time: '', delivery_cost: 0, notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.failed'));
    }
  };

  const addItemToOrder = () => {
    if (!selectedProduct || selectedQuantity < 1) return;
    const product = products.find(p => p.id === selectedProduct);
    if (!product) return;
    
    const existingIndex = newOrder.items.findIndex(i => i.product_id === selectedProduct);
    if (existingIndex >= 0) {
      const updatedItems = [...newOrder.items];
      updatedItems[existingIndex].quantity += selectedQuantity;
      setNewOrder(prev => ({ ...prev, items: updatedItems }));
    } else {
      setNewOrder(prev => ({
        ...prev,
        items: [...prev.items, {
          product_id: product.id,
          product_name: product.name,
          quantity: selectedQuantity,
          unit_price: product.retail_price
        }]
      }));
    }
    setSelectedProduct('');
    setSelectedQuantity(1);
  };

  const removeItemFromOrder = (index) => {
    setNewOrder(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const getStatusConfig = (status) => {
    return ORDER_STATUSES.find(s => s.value === status) || ORDER_STATUSES[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">{t('admin.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin`}>
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('admin.back')}
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('adminOrders.title')}</h1>
                <p className="text-sm text-gray-500">
                  {t('adminOrders.subtitle')}
                  {totalOrders > 0 && (
                    <span className="ml-2 text-emerald-700 font-medium" data-testid="orders-count-badge">
                      · Показано {orders.length}{orders.length < totalOrders ? ` из ${totalOrders}` : ''}
                      {(filters.dateFrom || filters.dateTo) && (
                        <button
                          type="button"
                          onClick={() => setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '' }))}
                          className="ml-2 text-xs underline text-emerald-800 hover:text-emerald-900"
                          data-testid="orders-reset-dates"
                        >сбросить период</button>
                      )}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button
                variant="outline"
                onClick={handleOpenBulkWaybills}
                className="border-slate-300 text-slate-800 hover:bg-slate-50"
                data-testid="orders-bulk-waybills-btn"
                title="Все накладные на завтра одним PDF (одна накладная на страницу A4)"
              >
                <Printer className="w-4 h-4 mr-1" />
                Накладные на завтра
              </Button>
              <Button onClick={() => setShowCreateOrder(true)} className="bg-emerald-700 hover:bg-emerald-800">
                <Plus className="w-4 h-4 mr-2" />
                {t('adminOrders.createOrder')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4">
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm text-gray-600">{t('adminOrders.search')}</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder={t('adminOrders.searchPlaceholder')}
                    value={filters.clientSearch}
                    onChange={(e) => setFilters(prev => ({ ...prev, clientSearch: e.target.value }))}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="w-[160px]">
                <Label className="text-sm text-gray-600">{t('adminOrders.status')}</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('adminOrders.allStatuses')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('adminOrders.allStatuses')}</SelectItem>
                    {ORDER_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[140px]">
                <Label className="text-sm text-gray-600">{t('adminOrders.clientType')}</Label>
                <Select value={filters.clientType} onValueChange={(v) => setFilters(prev => ({ ...prev, clientType: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('adminOrders.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('adminOrders.all')}</SelectItem>
                    <SelectItem value="B2B">B2B</SelectItem>
                    <SelectItem value="B2C">B2C</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[150px]">
                <Label className="text-sm text-gray-600">{t('adminOrders.dateFrom')}</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                />
              </div>

              <div className="w-[150px]">
                <Label className="text-sm text-gray-600">{t('adminOrders.dateTo')}</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                />
              </div>

              <div className="flex items-center gap-1 pb-0.5" data-testid="date-presets">
                {[
                  { label: 'Сегодня', days: 0 },
                  { label: 'Неделя', days: 6 },
                  { label: 'Месяц', days: 29 },
                ].map(p => (
                  <button
                    key={p.label}
                    type="button"
                    className="px-2 py-1 text-xs rounded border border-gray-200 bg-white hover:bg-emerald-50 hover:border-emerald-300 text-gray-700"
                    data-testid={`date-preset-${p.days}`}
                    onClick={() => {
                      const to = new Date();
                      const from = new Date();
                      from.setDate(to.getDate() - p.days);
                      const fmt = (d) => d.toISOString().slice(0, 10);
                      setFilters(prev => ({ ...prev, dateFrom: fmt(from), dateTo: fmt(to) }));
                    }}
                  >{p.label}</button>
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({
                  status: 'all',
                  clientType: 'all',
                  clientSearch: '',
                  dateFrom: '',
                  dateTo: '',
                  deliveryDate: '',
                })}
              >
                {t('adminOrders.reset')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">{t('adminOrders.orderNumber')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">{t('adminOrders.date')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">{t('adminOrders.client')}</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">{t('adminOrders.type')}</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">{t('adminOrders.amount')}</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">{t('adminOrders.status')}</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">{t('adminOrders.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        {t('adminOrders.noOrders')}
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => {
                      const statusConfig = getStatusConfig(order.status);
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm font-medium">{order.order_number}</span>
                              {order.source === '1c' && (
                                <span
                                  title="Заказ создан в 1С"
                                  className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-sky-100 text-sky-800 flex items-center gap-0.5"
                                  data-testid={`order-source-1c-${order.id}`}
                                >
                                  <Database className="w-3 h-3" />
                                  1С
                                </span>
                              )}
                              {order.sync_frozen_after && new Date(order.sync_frozen_after) < new Date() && (
                                <span
                                  title="Заказ заморожен: обновления из 1С блокируются"
                                  className="text-slate-500"
                                  data-testid={`order-frozen-${order.id}`}
                                >
                                  <Snowflake className="w-3 h-3" />
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(order.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-gray-900">{order.user_name}</div>
                              <div className="text-sm text-gray-500">{order.user_email}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant={order.user_role === 'B2B' ? 'default' : 'secondary'}>
                              {order.user_role}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {formatCurrency(order.total_amount)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Select
                              value={order.status}
                              onValueChange={(v) => handleStatusChange(order.id, v)}
                            >
                              <SelectTrigger className={`w-[150px] h-8 ${statusConfig.color}`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ORDER_STATUSES.map(s => (
                                  <SelectItem key={s.value} value={s.value}>
                                    {s.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setSelectedOrder(order); setShowOrderDetails(true); }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        <div className="mt-4 text-sm text-gray-500">
          {t('adminOrders.showing')} {filteredOrders.length} {t('adminOrders.of')} {orders.length} {t('adminOrders.ordersCount')}
        </div>
      </div>

      {/* Order Details Dialog */}
      <Dialog open={showOrderDetails} onOpenChange={setShowOrderDetails}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('adminOrders.order')} {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {(selectedOrder.source === '1c' || selectedOrder.last_synced_from_1c_at) && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-sky-50 border border-sky-200 text-sm" data-testid="order-1c-banner">
                  <Database className="w-4 h-4 text-sky-700 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sky-900 font-medium">
                      {selectedOrder.source === '1c' ? 'Заказ создан в 1С' : 'Заказ синхронизирован с 1С'}
                    </div>
                    <div className="text-xs text-sky-800 mt-0.5">
                      {selectedOrder.last_synced_from_1c_at && (
                        <>Последнее обновление из 1С: {new Date(selectedOrder.last_synced_from_1c_at).toLocaleString('ru')}. </>
                      )}
                      {selectedOrder.sync_frozen_after && new Date(selectedOrder.sync_frozen_after) < new Date()
                        ? '⛔ Заказ заморожен — обновления из 1С отклоняются.'
                        : selectedOrder.sync_frozen_after
                          ? `🕘 Заморозка активируется ${new Date(selectedOrder.sync_frozen_after).toLocaleString('ru')}.`
                          : 'Заморозка не установлена.'}
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">{t('adminOrders.client')}</Label>
                  <p className="font-medium">{selectedOrder.user_name}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.user_email}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.user_phone}</p>
                </div>
                <div>
                  <Label className="text-gray-500">{t('adminOrders.deliveryAddress')}</Label>
                  <p className="text-sm">{selectedOrder.user_address || '—'}</p>
                </div>
              </div>

              <div>
                <Label className="text-gray-500 mb-2 block">{t('adminOrders.orderItems')}</Label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-sm">{t('adminOrders.item')}</th>
                        <th className="text-center px-3 py-2 text-sm">{t('adminOrders.qty')}</th>
                        <th className="text-right px-3 py-2 text-sm">{t('adminOrders.price')}</th>
                        <th className="text-right px-3 py-2 text-sm">{t('adminOrders.total')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedOrder.items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">{item.product_name}</td>
                          <td className="px-3 py-2 text-center">{item.quantity}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(item.unit_price)}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 font-medium">
                      <tr>
                        <td colSpan={3} className="px-3 py-2 text-right">Сумма товаров:</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(selectedOrder.total_amount)}</td>
                      </tr>
                      {selectedOrder.delivery_fee > 0 && (
                        <tr>
                          <td colSpan={3} className="px-3 py-2 text-right">Доставка:</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(selectedOrder.delivery_fee)}</td>
                        </tr>
                      )}
                      <tr className="text-lg">
                        <td colSpan={3} className="px-3 py-2 text-right">{t('adminOrders.total')}:</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(selectedOrder.final_amount || selectedOrder.total_amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Delivery Info */}
              {selectedOrder.delivery_type === 'delivery' && (
                <div className="border-t pt-4">
                  <Label className="text-gray-500 mb-3 block flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Информация о доставке
                  </Label>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Дата доставки:</span>
                      <p className="font-medium">{selectedOrder.delivery_date || '—'} {selectedOrder.delivery_time || ''}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Расстояние:</span>
                      <p className="font-medium">{selectedOrder.delivery_distance_km ? `${selectedOrder.delivery_distance_km} км` : '—'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Время готовки:</span>
                      <p className="font-medium">{selectedOrder.estimated_cooking_time ? `${selectedOrder.estimated_cooking_time} мин` : 'Не указано'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Время доставки:</span>
                      <p className="font-medium">{selectedOrder.estimated_delivery_time ? `~${selectedOrder.estimated_delivery_time} мин` : '—'}</p>
                    </div>
                  </div>
                  
                  {selectedOrder.tracking_link && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <span className="text-gray-500 text-sm">Ссылка отслеживания:</span>
                      <a 
                        href={selectedOrder.tracking_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Открыть трекинг
                      </a>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-4 flex-wrap">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openCookingTimeDialog(selectedOrder)}
                      data-testid="order-cooking-time-btn"
                    >
                      <Timer className="w-4 h-4 mr-1" />
                      Время готовки
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openTrackingDialog(selectedOrder)}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid="order-tracking-btn"
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      {selectedOrder.tracking_link ? 'Изменить трекинг' : 'Добавить трекинг'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRebuildRoute(selectedOrder)}
                      disabled={rebuildingRoute || !selectedOrder.delivery_date}
                      className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      data-testid="order-rebuild-route-btn"
                      title={!selectedOrder.delivery_date ? 'Не указана дата доставки' : 'Пересчитать маршрут на дату доставки и отправить 3 варианта в Telegram'}
                    >
                      <Route className="w-4 h-4 mr-1" />
                      {rebuildingRoute ? 'Отправляю…' : 'Пересчитать маршрут'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenWaybill(selectedOrder)}
                      className="text-slate-800 border-slate-300 hover:bg-slate-50"
                      data-testid="order-waybill-btn"
                      title="Открыть накладную (Форма 3-2) для печати в новой вкладке"
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      Накладная
                    </Button>
                    {(selectedOrder.sync_frozen_after || selectedOrder.source === '1c' || selectedOrder.last_synced_from_1c_at) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnfreeze1C(selectedOrder)}
                        className="text-sky-800 border-sky-300 hover:bg-sky-50"
                        data-testid="order-unfreeze-1c-btn"
                        title={selectedOrder.sync_frozen_after ? 'Снять заморозку и разрешить следующий пуш из 1С' : 'Заказ синхронизируется с 1С'}
                      >
                        <Snowflake className="w-4 h-4 mr-1" />
                        Подтянуть из 1С
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Waybill / 1C — always available, incl. pickup orders */}
              {selectedOrder.delivery_type !== 'delivery' && (
                <div className="border-t pt-4">
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenWaybill(selectedOrder)}
                      className="text-slate-800 border-slate-300 hover:bg-slate-50"
                      data-testid="order-waybill-btn-pickup"
                      title="Открыть накладную (Форма 3-2) для печати в новой вкладке"
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      Накладная
                    </Button>
                    {(selectedOrder.sync_frozen_after || selectedOrder.source === '1c' || selectedOrder.last_synced_from_1c_at) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnfreeze1C(selectedOrder)}
                        className="text-sky-800 border-sky-300 hover:bg-sky-50"
                        data-testid="order-unfreeze-1c-btn-pickup"
                        title={selectedOrder.sync_frozen_after ? 'Снять заморозку и разрешить следующий пуш из 1С' : 'Заказ синхронизируется с 1С'}
                      >
                        <Snowflake className="w-4 h-4 mr-1" />
                        Подтянуть из 1С
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cooking Time Dialog */}
      <Dialog open={showCookingTimeDialog} onOpenChange={setShowCookingTimeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Timer className="w-5 h-5" />
              Время готовки
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Укажите время приготовления заказа. Клиент увидит это время.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                value={cookingTime}
                onChange={(e) => setCookingTime(parseInt(e.target.value) || 0)}
                className="w-24"
                min={5}
                max={120}
              />
              <span className="text-gray-600">минут</span>
            </div>
            <div className="flex gap-2">
              {[10, 15, 20, 30, 45, 60].map(time => (
                <Button
                  key={time}
                  variant={cookingTime === time ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCookingTime(time)}
                  className={cookingTime === time ? "bg-emerald-700" : ""}
                >
                  {time} мин
                </Button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowCookingTimeDialog(false)}>
              Отмена
            </Button>
            <Button onClick={handleSetCookingTime} className="bg-emerald-700 hover:bg-emerald-800">
              Сохранить
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tracking Link Dialog */}
      <Dialog open={showTrackingDialog} onOpenChange={setShowTrackingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Ссылка отслеживания
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Вставьте ссылку на отслеживание от Яндекс Курьера, Wolt или Glovo. Клиент увидит эту ссылку в своём заказе.
            </p>
            <Input
              type="url"
              value={trackingLink}
              onChange={(e) => setTrackingLink(e.target.value)}
              placeholder="https://..."
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowTrackingDialog(false)}>
              Отмена
            </Button>
            <Button 
              onClick={handleSetTrackingLink} 
              disabled={!trackingLink}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Сохранить и отправить
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Order Dialog */}
      <Dialog open={showCreateOrder} onOpenChange={setShowCreateOrder}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('adminOrders.createOrder')}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateOrder} className="space-y-4">
            <div>
              <Label>{t('adminOrders.client')} *</Label>
              <Select
                value={newOrder.user_id}
                onValueChange={(v) => setNewOrder(prev => ({ ...prev, user_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('adminOrders.selectClient')} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      <span className="font-medium">{client.company_name || client.full_name || client.email}</span>
                      <span className="ml-2 text-gray-500">({client.role})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('adminOrders.addItem')}</Label>
              <div className="flex gap-2 mt-1">
                <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder={t('adminOrders.selectItem')} />
                  </SelectTrigger>
                  <SelectContent>
                    {products.filter(p => p.is_active).map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} — {formatCurrency(product.retail_price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min="1"
                  value={selectedQuantity}
                  onChange={(e) => setSelectedQuantity(parseInt(e.target.value) || 1)}
                  className="w-24"
                  placeholder={t('adminOrders.qty')}
                />
                <Button type="button" onClick={addItemToOrder}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {newOrder.items.length > 0 && (
              <div className="border rounded-lg p-3">
                <Label className="text-sm text-gray-500">{t('adminOrders.itemsInOrder')}</Label>
                <div className="space-y-2 mt-2">
                  {newOrder.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1 border-b last:border-0">
                      <span>{item.product_name} x {item.quantity}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">{formatCurrency(item.unit_price * item.quantity)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItemFromOrder(idx)}
                        >
                          <XCircle className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="pt-2 font-medium text-right">
                    {t('adminOrders.subtotal')}: {formatCurrency(newOrder.items.reduce((sum, i) => sum + i.unit_price * i.quantity, 0))}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('adminOrders.deliveryDate')} *</Label>
                <Input
                  type="date"
                  value={newOrder.delivery_date}
                  onChange={(e) => setNewOrder(prev => ({ ...prev, delivery_date: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label>{t('adminOrders.deliveryTime')} *</Label>
                <Input
                  type="time"
                  value={newOrder.delivery_time}
                  onChange={(e) => setNewOrder(prev => ({ ...prev, delivery_time: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateOrder(false)}>
                {t('adminOrders.cancel')}
              </Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800">
                {t('adminOrders.createOrder')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminOrders;
